import React from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  NOTIFICATION_COLORS,
  NOTIFICATION_AVATAR,
  NOTIFICATION_AVATAR_DESKTOP,
  NOTIFICATION_UNREAD_DOT,
  NOTIFICATION_UNREAD_DOT_DESKTOP,
} from '@/constants/residents/mobile/notificationColors';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';

interface NotificationAvatarProps {
  /** Whether to show the unread indicator dot */
  showUnreadDot?: boolean;
  /** Custom size override (default: 46 for mobile, 36 for desktop) */
  size?: number;
  /** Custom icon size override (default: 20 for mobile, 18 for desktop) */
  iconSize?: number;
  /** Variant for different notification types */
  variant?: 'property' | 'marketplace' | 'message';
  /** Optional image URL to display instead of icon */
  imageUrl?: string | null;
  /** Platform to use for styling (default: 'mobile') */
  platform?: 'mobile' | 'desktop';
}

/**
 * NotificationAvatar
 * 
 * Shared avatar component for notification cards and rows.
 * Shows a property or marketplace icon inside a circular background with an optional unread indicator.
 * Can also display an image (e.g., listing image) if imageUrl is provided.
 * Supports both mobile and desktop styling via the platform prop.
 */
export const NotificationAvatar: React.FC<NotificationAvatarProps> = ({
  showUnreadDot = false,
  size,
  iconSize,
  variant = 'property',
  imageUrl,
  platform = 'mobile',
}) => {
  // Use platform-specific defaults
  const isDesktop = platform === 'desktop';
  const avatarSize = size ?? (isDesktop ? NOTIFICATION_AVATAR_DESKTOP.size : NOTIFICATION_AVATAR.size);
  const iconSizeValue = iconSize ?? (isDesktop ? 18 : NOTIFICATION_AVATAR.iconSize);
  const borderRadius = avatarSize / 2;

  // Select icon based on variant (used as fallback when no image)
  const IconComponent = variant === 'message'
    ? RESIDENTS_SVG_COMPONENTS.CHAT_ICON
    : variant === 'marketplace' 
      ? RESIDENTS_SVG_COMPONENTS.SUPERMARKET 
      : RESIDENTS_SVG_COMPONENTS.PROPERTY;

  // Platform-specific styling
  const avatarBackgroundColor = isDesktop 
    ? RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND 
    : NOTIFICATION_COLORS.avatarBackground;
  
  const iconColor = isDesktop 
    ? RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE 
    : NOTIFICATION_COLORS.iconDefault;

  // Desktop unread dot has offset and border
  const unreadDotStyle = isDesktop
    ? {
        position: 'absolute' as const,
        top: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetTop,
        left: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetLeft,
        width: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
        height: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
        borderRadius: NOTIFICATION_UNREAD_DOT_DESKTOP.borderRadius,
        backgroundColor: NOTIFICATION_COLORS.unreadDot,
        borderWidth: 1,
        borderColor: NOTIFICATION_COLORS.unreadDot,
      }
    : styles.unreadDot;

  return (
    <View style={styles.container}>
      <View style={[styles.avatar, { width: avatarSize, height: avatarSize, borderRadius, backgroundColor: avatarBackgroundColor }]}>
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl }}
            style={[styles.image, { width: avatarSize, height: avatarSize, borderRadius }]}
            resizeMode="cover"
          />
        ) : (
          <IconComponent
            width={iconSizeValue}
            height={iconSizeValue}
            color={iconColor}
          />
        )}
      </View>
      {showUnreadDot && <View style={unreadDotStyle} accessibilityLabel="Unread notification" />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  avatar: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: NOTIFICATION_UNREAD_DOT.size,
    height: NOTIFICATION_UNREAD_DOT.size,
    borderRadius: NOTIFICATION_UNREAD_DOT.borderRadius,
    backgroundColor: NOTIFICATION_COLORS.unreadDot,
  },
});

export default NotificationAvatar;
